import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { errors, ProfileSummary, paxRoute, Timestamp, UserId } from "../../common";
import {
  automodUnavailable,
  moderationRefused,
  surfaceBanned,
} from "../../moderation/v1alpha1/endpoints";
import { noSuchPreset, teamRefusals } from "./refusals";
import { PresetSlugParams } from "./schemas";

/**
 * What a preset's roles can let its members do. Editing a draft's world is checked by its draft
 * session on the preset server; the rest by the API.
 */
export const PRESET_PERMISSIONS = [
  "editMap",
  "editPerspectiveMaps",
  "editSkills",
  "editPerspectives",
  "editRecommendations",
  "editPage",
  "createDrafts",
  "discardDrafts",
  "renamePreset",
  "publish",
  "invitePeople",
  "removePeople",
  "manageRoles",
  "issueShares",
] as const;

export const PresetPermission = z.enum(PRESET_PERMISSIONS).meta({ id: "PresetPermission" });
export type PresetPermission = z.infer<typeof PresetPermission>;

/** The shares a new preset issues its owner, who earns all of its revenue until they share some. */
export const FIRST_SHARE_SUPPLY = 10_000_000;

interface DefaultRole {
  roleId: string;
  name: string;
  rank: number;
  permissions: PresetPermission[];
  /** For `issueShares`: how many a member may issue in a window of days. */
  issueLimit: { shares: number; days: number } | null;
}

const EDITING: PresetPermission[] = [
  "editMap",
  "editPerspectiveMaps",
  "editSkills",
  "editPerspectives",
  "editRecommendations",
  "editPage",
  "createDrafts",
];

/**
 * The roles every preset starts with. Viewer is the everyone role: every member holds it, it ranks
 * 0, and it can be renamed and given permissions but not deleted.
 */
export const DEFAULT_PRESET_ROLES: DefaultRole[] = [
  { roleId: "viewer", name: "Viewer", rank: 0, permissions: [], issueLimit: null },
  { roleId: "editor", name: "Editor", rank: 50, permissions: EDITING, issueLimit: null },
  {
    roleId: "manager",
    name: "Manager",
    rank: 100,
    permissions: [
      ...EDITING,
      "discardDrafts",
      "renamePreset",
      "publish",
      "invitePeople",
      "removePeople",
      "manageRoles",
      "issueShares",
    ],
    issueLimit: { shares: 500_000, days: 30 },
  },
];

/** The role every member holds, ranked 0. */
export const EVERYONE_ROLE = "viewer";

export const PresetRoleId = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,47}$/)
  .meta({ id: "PresetRoleId", description: "A role's id in its preset.", example: "editor" });
export type PresetRoleId = z.infer<typeof PresetRoleId>;

export const IssueLimit = z
  .strictObject({
    shares: z.number().int().positive(),
    days: z.number().int().positive().max(365),
  })
  .meta({
    id: "IssueLimit",
    description: "How many shares a member may issue in a window of days.",
  });
export type IssueLimit = z.infer<typeof IssueLimit>;

const RoleName = z.string().trim().min(1).max(40);
const RoleRank = z.number().int().min(1).max(1000);

export const PresetRole = z
  .strictObject({
    roleId: PresetRoleId,
    name: RoleName,
    rank: z.number().int().min(0).max(1000),
    permissions: z.array(PresetPermission),
    issueLimit: IssueLimit.nullable().meta({
      description: "For `issueShares`: its limit, or null for none.",
    }),
  })
  .meta({ id: "PresetRole" });
export type PresetRole = z.infer<typeof PresetRole>;

export const TeamMember = z
  .strictObject({
    profile: ProfileSummary,
    roleIds: z.array(PresetRoleId).meta({ description: "Their roles besides the everyone role." }),
    canTransferShares: z.boolean(),
    invitedBy: UserId.nullable(),
    invitedAt: Timestamp,
    joinedAt: Timestamp.nullable().meta({ description: "Null while their invitation waits." }),
  })
  .meta({ id: "TeamMember" });
export type TeamMember = z.infer<typeof TeamMember>;

export const PresetTeam = z
  .strictObject({
    owner: ProfileSummary,
    roles: z.array(PresetRole).meta({ description: "Lowest rank first." }),
    members: z
      .array(TeamMember)
      .meta({ description: "Everyone but the owner, invited or joined." }),
  })
  .meta({ id: "PresetTeam", description: "Who works on a preset, and what each may do." });
export type PresetTeam = z.infer<typeof PresetTeam>;

/**
 * The team as its drafts read who may change what: its owner, its roles, and everyone who has
 * joined with the roles they hold besides the everyone role.
 */
export const DraftTeam = z
  .strictObject({
    ownerId: UserId,
    roles: z.array(PresetRole),
    members: z.array(z.strictObject({ userId: UserId, roleIds: z.array(PresetRoleId) })),
  })
  .meta({ id: "DraftTeam", description: "Who may change what in a preset's drafts." });
export type DraftTeam = z.infer<typeof DraftTeam>;

export const InviteRequest = z
  .strictObject({ userId: UserId, roleIds: z.array(PresetRoleId).max(20) })
  .meta({ id: "InviteRequest", description: "Who to invite, and the roles they'll hold." });
export type InviteRequest = z.infer<typeof InviteRequest>;

export const UpdateMemberRequest = z
  .strictObject({
    roleIds: z.array(PresetRoleId).max(20).optional(),
    canTransferShares: z.boolean().optional(),
  })
  .refine(update => update.roleIds !== undefined || update.canTransferShares !== undefined, {
    message: "say what changes",
  })
  .meta({ id: "UpdateMemberRequest" });
export type UpdateMemberRequest = z.infer<typeof UpdateMemberRequest>;

export const CreateRoleRequest = z
  .strictObject({
    name: RoleName,
    rank: RoleRank,
    permissions: z.array(PresetPermission),
    issueLimit: IssueLimit.nullable(),
  })
  .meta({ id: "CreateRoleRequest" });
export type CreateRoleRequest = z.infer<typeof CreateRoleRequest>;

export const UpdateRoleRequest = z
  .strictObject({
    name: RoleName.optional(),
    rank: RoleRank.optional(),
    permissions: z.array(PresetPermission).optional(),
    issueLimit: IssueLimit.nullable().optional(),
  })
  .refine(update => Object.keys(update).length > 0, { message: "say what changes" })
  .meta({ id: "UpdateRoleRequest", description: "The everyone role keeps its rank of 0." });
export type UpdateRoleRequest = z.infer<typeof UpdateRoleRequest>;

export const TransferOwnershipRequest = z
  .strictObject({ userId: UserId })
  .meta({ id: "TransferOwnershipRequest", description: "A member who has joined." });
export type TransferOwnershipRequest = z.infer<typeof TransferOwnershipRequest>;

export const TEAM_EVENT_TYPES = [
  "member_invited",
  "member_joined",
  "member_removed",
  "member_roles_changed",
  "share_transfers_allowed",
  "share_transfers_disallowed",
  "role_created",
  "role_changed",
  "role_deleted",
  "ownership_transferred",
] as const;

export const TeamEvent = z
  .strictObject({
    id: z.uuid(),
    eventType: z.enum(TEAM_EVENT_TYPES),
    actorId: UserId,
    userId: UserId.nullable(),
    roleId: z.string().nullable(),
    after: z.record(z.string(), z.json()),
    occurredAt: Timestamp,
  })
  .meta({ id: "TeamEvent", description: "One change to the team." });
export type TeamEvent = z.infer<typeof TeamEvent>;

export const ListTeamEventsQuery = z.strictObject({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.uuid().optional().meta({ description: "The `nextCursor` of the page before." }),
});
export type ListTeamEventsQuery = z.infer<typeof ListTeamEventsQuery>;

export const ListTeamEventsResponse = z
  .strictObject({ events: z.array(TeamEvent), nextCursor: z.uuid().nullable() })
  .meta({ id: "ListTeamEventsResponse", description: "Newest first." });
export type ListTeamEventsResponse = z.infer<typeof ListTeamEventsResponse>;

const MemberParams = PresetSlugParams.extend({ userId: UserId });
const RoleParams = PresetSlugParams.extend({ roleId: PresetRoleId });

const refused = {
  ...errors.badRequest,
  ...teamRefusals,
  ...noSuchPreset,
  CONFLICT: { message: "The team has changed since." },
} as const;

export const getTeam = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/presets/v1alpha1/{slug}/team",
      operationId: "presets.team.get",
      summary: "Read a preset's team.",
      description: "Its owner, its roles, and everyone invited or joined.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...teamRefusals, ...noSuchPreset })
  .input(z.object({ params: PresetSlugParams }))
  .output(PresetTeam);

export const invite = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/presets/v1alpha1/{slug}/team/invitations",
      operationId: "presets.team.invitations.create",
      summary: "Invite someone to a preset's team.",
      description:
        "Moderates every draft opened since it was last moderated, each as a whole as it would be published, then invites the account with the roles given, each ranked below yours. A draft automod refuses, or that can't be compiled to check, stops the invitation. Takes `invitePeople`.",
      inputStructure: "detailed",
      successStatus: 201,
    }),
  )
  .errors({
    ...refused,
    ...surfaceBanned,
    ...moderationRefused,
    ...automodUnavailable,
    TOO_MANY_REQUESTS: { message: "Too many invitations at once; try again shortly." },
  })
  .input(z.object({ params: PresetSlugParams, body: InviteRequest }))
  .output(PresetTeam);

export const acceptInvitation = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/presets/v1alpha1/{slug}/team/invitation/accept",
      operationId: "presets.team.invitation.accept",
      summary: "Join a preset's team you were invited to.",
      inputStructure: "detailed",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    ...surfaceBanned,
    NOT_FOUND: { message: "No invitation to this preset is waiting for you." },
  })
  .input(z.object({ params: PresetSlugParams }))
  .output(z.strictObject({}));

export const declineInvitation = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/presets/v1alpha1/{slug}/team/invitation",
      operationId: "presets.team.invitation.delete",
      summary: "Turn down an invitation to a preset's team.",
      inputStructure: "detailed",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...teamRefusals,
    NOT_FOUND: { message: "No invitation to this preset is waiting for you." },
  })
  .input(z.object({ params: PresetSlugParams }))
  .output(z.strictObject({}));

export const updateMember = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/presets/v1alpha1/{slug}/team/members/{userId}",
      operationId: "presets.team.members.update",
      summary: "Change a member's roles, or whether they may transfer shares.",
      description:
        "Giving or taking a role takes `manageRoles`, and both the member and the role ranked below you. Only the owner lets a member transfer their shares.",
      inputStructure: "detailed",
    }),
  )
  .errors(refused)
  .input(z.object({ params: MemberParams, body: UpdateMemberRequest }))
  .output(PresetTeam);

export const removeMember = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/presets/v1alpha1/{slug}/team/members/{userId}",
      operationId: "presets.team.members.delete",
      summary: "Remove someone from a preset's team, or leave it.",
      description:
        "Removing someone else takes `removePeople`, and them ranked below you. Anyone but the owner may leave. Their shares are reclaimed for the owner.",
      inputStructure: "detailed",
    }),
  )
  .errors(refused)
  .input(z.object({ params: MemberParams }))
  .output(z.strictObject({}));

export const createRole = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "POST",
      path: "/api/presets/v1alpha1/{slug}/team/roles",
      operationId: "presets.team.roles.create",
      summary: "Create a role.",
      description: "Takes `manageRoles`. It ranks below you, and grants only permissions you hold.",
      inputStructure: "detailed",
      successStatus: 201,
    }),
  )
  .errors(refused)
  .input(z.object({ params: PresetSlugParams, body: CreateRoleRequest }))
  .output(PresetTeam);

export const updateRole = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PATCH",
      path: "/api/presets/v1alpha1/{slug}/team/roles/{roleId}",
      operationId: "presets.team.roles.update",
      summary: "Change a role.",
      description:
        "Takes `manageRoles` and the role ranked below you. You can grant or take only permissions you hold.",
      inputStructure: "detailed",
    }),
  )
  .errors(refused)
  .input(z.object({ params: RoleParams, body: UpdateRoleRequest }))
  .output(PresetTeam);

export const deleteRole = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "DELETE",
      path: "/api/presets/v1alpha1/{slug}/team/roles/{roleId}",
      operationId: "presets.team.roles.delete",
      summary: "Delete a role.",
      description:
        "Takes it from everyone who holds it. Takes `manageRoles`, the role and all its holders ranked below you. The everyone role can't be deleted.",
      inputStructure: "detailed",
    }),
  )
  .errors(refused)
  .input(z.object({ params: RoleParams }))
  .output(PresetTeam);

export const transferOwnership = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: "suspicious", cost: "write" }),
    openapi({
      method: "PUT",
      path: "/api/presets/v1alpha1/{slug}/team/owner",
      operationId: "presets.team.owner.set",
      summary: "Hand a preset to another member.",
      description:
        "Only the owner. The member becomes the owner, and the owner becomes a member holding every role. Everyone keeps their shares.",
      inputStructure: "detailed",
    }),
  )
  .errors(refused)
  .input(z.object({ params: PresetSlugParams, body: TransferOwnershipRequest }))
  .output(PresetTeam);

export const listTeamEvents = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/presets/v1alpha1/{slug}/team/events",
      operationId: "presets.team.events.list",
      summary: "Read how a preset's team came to be.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...teamRefusals, ...noSuchPreset })
  .input(z.object({ params: PresetSlugParams, query: ListTeamEventsQuery }))
  .output(ListTeamEventsResponse);
